/**
 * TileStage.tsx — WEB.4.UI.5
 *
 * Per-tile micro scene shown when the current player lands on a tile.
 * Implements the approved mockup: design-mockups/web.4.ui.5-tilestage.html
 *
 * Layer stack (z-index, low → high):
 *   0  ts-bg       — background image, object-fit:cover + ambient-pan
 *   1  ts-vignette — dark radial gradient overlay
 *   2  ts-platform — horizontal strip of tiled platform PNG
 *   3  ts-foreground — facility sprite + player tokens with drop-in animation
 *   5  ts-header   — tile name, round chip, ownership badge
 *
 * BuildMenu (z:200 fixed overlay) mounts on top — TileStage stays visible
 * behind the dim backdrop. No extra handling needed here.
 *
 * VISIBLE WHEN: turnPhase === 'landed'
 * TEST PATH: roll dice → player lands on any non-Start tile → TileStage renders.
 *
 * Terrain key resolution (mirrors Unity TileStageManager.TerrainKey):
 *   Coast → coastline
 *   all others → tileType.toLowerCase()
 *   Missing platforms (forest, jail, trading, teleport): CSS gradient fallback
 *   Missing bgs (trading, teleport): ts-bg-fallback div instead of <img>
 */

import type React from "react";
import tilePositionsData from "../../../../../data/board/tile-positions.json";
import { FACILITIES } from "../../store/facilities.js";
import { SEAT_COLORS, type SeatIndex, useGameStore } from "../../store/game-store.js";
import styles from "./TileStage.module.css";

// ---------------------------------------------------------------------------
// Tile lookup
// ---------------------------------------------------------------------------

type TileEntry = { index: number; type: string; x: number; y: number };
const _tileTypeByIndex: Record<number, string> = Object.fromEntries(
  (tilePositionsData.tiles as TileEntry[]).map((t) => [t.index, t.type]),
);

// ---------------------------------------------------------------------------
// Terrain key helpers
// ---------------------------------------------------------------------------

/**
 * Resolve the terrain asset key from a tile type string.
 * Coast → coastline. All others → lowercase.
 */
/** Tile types where TileStage is NOT rendered (handled by other surfaces). */
const NON_STAGE_TILE_TYPES = new Set(["Start", "Event", "Policy"]);

/** Mirrors Unity TileStageManager.cs:115-121 TerrainKey() switch.
 *  Coast → coastline (asset filename uses long form).
 *  Jail/Trading/Teleport → use their own authentic assets (Unity Resources
 *  sync 2026-05-11 added bg_jail/bg_trading/bg_teleport + platform_*).
 *  Earlier urban-substitute lock superseded by user. */
function resolveTerrainKey(tileType: string): string {
  switch (tileType) {
    case "Coast":
      return "coastline";
    case "Jail":
      return "jail";
    case "Trading":
      return "trading";
    case "Teleport":
      return "teleport";
    default:
      return tileType.toLowerCase();
  }
}

/** Tile types whose platform PNG is known-missing → use CSS gradient fallback.
 *  Empty after 2026-05-11 Unity Resources sync — all 12 platforms now exist
 *  (forest/jail/teleport/trading added). Kept as a safety hook for future
 *  asset deletions or platform additions before assets land. */
const PLATFORM_FALLBACK_TYPES = new Set<string>([]);

/** Tile types whose background PNG is known-missing → use flat-gradient fallback.
 *  Empty after 2026-05-11 Unity Resources sync — bg_trading/bg_teleport added. */
const BG_FALLBACK_TYPES = new Set<string>([]);

// ---------------------------------------------------------------------------
// Platform strip sub-component
// ---------------------------------------------------------------------------

interface PlatformStripProps {
  terrainKey: string;
}

function PlatformStrip({ terrainKey }: PlatformStripProps): React.ReactElement {
  if (PLATFORM_FALLBACK_TYPES.has(terrainKey)) {
    // CSS gradient band — warm earth tones tuned per biome
    return (
      <div
        className={`${styles.platformStrip} ${styles.platformFallback} ${styles[`fallback_${terrainKey}` as keyof typeof styles] ?? styles.fallback_jail}`}
        aria-label={`${terrainKey} platform (gradient fallback)`}
      />
    );
  }

  const src = `/images/terrain/platforms_seamless/platform_${terrainKey}.png`;
  // Repeat 12× — sufficient for any viewport width up to ~1920px
  const repeats = Array.from({ length: 12 }, (_, i) => i);

  return (
    <div className={styles.platformStrip}>
      {repeats.map((i) => (
        <img
          key={i}
          src={src}
          alt=""
          className={styles.platformImg}
          onError={(e) => {
            // Fallback: hide individually if this tile's PNG load fails
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TileStage — public component
// ---------------------------------------------------------------------------

export function TileStage(): React.ReactElement | null {
  const turnPhase = useGameStore((s) => s.turnPhase);
  const currentSeat = useGameStore((s) => s.currentSeat);
  const numPlayers = useGameStore((s) => s.numPlayers);
  const playerTiles = useGameStore((s) => s.playerTiles);
  const round = useGameStore((s) => s.round);
  const ownedBy = useGameStore((s) => s.ownedBy);
  const builtFacilities = useGameStore((s) => s.builtFacilities);

  // Visibility gates (codex R1 HIGH 1):
  //   - turnPhase must be 'landed'
  //   - Start/Event/Policy tiles handled by other surfaces (HexBoard / CardDetailModal)
  if (turnPhase !== "landed") return null;
  const tileIdx = playerTiles[currentSeat] ?? 0;
  const tileType = _tileTypeByIndex[tileIdx] ?? "Plains";
  if (NON_STAGE_TILE_TYPES.has(tileType)) return null;

  const terrainKey = resolveTerrainKey(tileType);

  // Which players are on this tile (for token rendering)?
  // Always include current seat; also include others who share the tile.
  // Present seats only (< numPlayers) — absent seats keep playerTiles=0 and would
  // render phantom pawns on tile 0 (the ghost-at-origin class). Default 4 → all four.
  const tokensOnTile: SeatIndex[] = ([0, 1, 2, 3] as SeatIndex[]).filter(
    (s) => s < numPlayers && playerTiles[s] === tileIdx,
  );

  // Ownership + facility
  const ownerSeat = ownedBy[tileIdx];
  const facilityId = builtFacilities[tileIdx];
  const facilityDef =
    facilityId !== undefined ? FACILITIES.find((f) => f.id === facilityId) : undefined;

  // Header seat color strip uses current seat color
  const currentSeatColor = SEAT_COLORS[currentSeat];

  // Owner badge
  const ownerLabel =
    ownerSeat !== undefined && facilityDef !== undefined
      ? `${facilityDef.name} · P${ownerSeat + 1}`
      : ownerSeat !== undefined
        ? `Owned by P${ownerSeat + 1}`
        : null;

  const bgSrc = `/images/terrain/backgrounds/bg_${terrainKey}.jpg`;
  const hasBgFallback = BG_FALLBACK_TYPES.has(terrainKey);

  // codex R1 MED 5: key forces remount on tile/seat change so drop-in animation replays
  const stageKey = `${tileIdx}-${currentSeat}`;

  return (
    <div key={stageKey} className={styles.tileStage} data-testid="tile-stage">
      {/* LAYER 0 — Background */}
      {hasBgFallback ? (
        <div className={styles.bgFallback} data-testid="ts-bg-fallback" />
      ) : (
        <img
          className={styles.bg}
          src={bgSrc}
          alt=""
          data-testid="ts-bg"
          onError={(e) => {
            // Hide broken bg gracefully; bgFallback div not present — stage shows dark
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
        />
      )}

      {/* LAYER 1 — Vignette */}
      <div className={styles.vignette} aria-hidden="true" />

      {/* LAYER 2 — Platform strip */}
      <PlatformStrip terrainKey={terrainKey} />

      {/* LAYER 3 — Foreground: facility + tokens */}
      <div className={styles.foreground}>
        {facilityDef !== undefined && (
          <div className={styles.facility} title={facilityDef.name} data-testid="ts-facility">
            <img
              // codex R1 HIGH 3: use cropped variant (transparent borders trimmed) NOT original
              src={`/images/facilities_cropped/facility_${facilityDef.id}.png`}
              alt={facilityDef.name}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.opacity = "0.3";
              }}
            />
          </div>
        )}

        <div className={styles.tokens} data-testid="ts-tokens">
          {tokensOnTile.map((s) => (
            <div
              key={s}
              className={`${styles.token} ${styles[`token_p${s + 1}` as keyof typeof styles] ?? ""}`}
              style={{
                background: SEAT_COLORS[s],
                // codex R1 MED 5: stagger by seat-index (not render-order) per spec
                animationDelay: `${s * 0.08}s`,
              }}
              data-testid={`ts-token-p${s + 1}`}
              aria-label={`Player ${s + 1}`}
            >
              P{s + 1}
            </div>
          ))}
        </div>
      </div>

      {/* LAYER 4 — Chrome: header */}
      <div className={styles.header} data-testid="ts-header">
        <div className={styles.headerLeft}>
          <div
            className={styles.seatStrip}
            style={{ background: currentSeatColor }}
            data-testid="ts-seat-strip"
          />
          <span className={styles.tileTitle} data-testid="ts-tile-title">
            Tile {String(tileIdx).padStart(2, "0")} · {tileType}
          </span>
        </div>

        <div className={styles.infoChip} data-testid="ts-info-chip">
          <span className={styles.chip} data-testid="ts-round-chip">
            Round {round}
          </span>
          {ownerLabel !== null ? (
            <span className={`${styles.chip} ${styles.chipOwned}`} data-testid="ts-owner-badge">
              <span
                className={styles.ownerPip}
                style={{ background: ownerSeat !== undefined ? SEAT_COLORS[ownerSeat] : undefined }}
              />
              {ownerLabel}
            </span>
          ) : (
            <span className={styles.chip} data-testid="ts-unowned-chip">
              Unowned
            </span>
          )}
        </div>
      </div>

      {/* Empty slot for future coin-fly overlay (toll animation, WEB.4.UI.6+) */}
      <div className={styles.overlayCoins} aria-hidden="true" />
    </div>
  );
}
